import { useEffect, useState } from "react";
import type { Authorization, DeviceStatus } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { CodeWindow } from "../codes/CodeDialog";

interface Props {
  /** The owner's Beheer code: only the owner may unlock deleting. */
  beheer: Authorization;
  /** Why the code is asked, under the title (deleting needs none). */
  note?: string | undefined;
  /**
   * The code was right: the till keeps it in memory until Beheer closes and sends it with each delete, or
   * sends it once with a key of Bancontact Pro.
   */
  onUnlocked: (code: string) => void;
  onClose: () => void;
}

/**
 * The small ⚙ in Beheer › Bestellingen asks for the special admin code (Furkan, 01/10/2026): six
 * digits that only Tafo gives, on request. A shop without it has no delete function; the window then
 * only says where to get it. Saving or removing a key of Bancontact Pro in Beheer › Algemeen asks it
 * too (security review, 30/10/2026).
 */
export function AdminCodeDialog({ beheer, note, onUnlocked, onClose }: Props) {
  const { api, status } = useTill();
  // asked again on opening: Tafo may have given (or withdrawn) the code since the till started
  const [given, setGiven] = useState(status.adminCode === true);
  useEffect(() => {
    const controller = new AbortController();
    api
      .request<DeviceStatus>("GET", "/device", { signal: controller.signal })
      .then((fresh) => {
        setGiven(fresh.adminCode === true);
      })
      .catch(() => {
        // offline or closed: keep what the till knew
      });
    return () => {
      controller.abort();
    };
  }, [api]);

  if (!given) {
    return (
      <Dialog
        title={t.codes.adminCode}
        size="narrow"
        onClose={onClose}
        footer={
          <>
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose} data-autofocus>
              {t.orders.back}
            </button>
          </>
        }
      >
        {note !== undefined && <p className="note">{note}</p>}
        <p className="note">{t.codes.adminCodeMissing}</p>
      </Dialog>
    );
  }

  return (
    <CodeWindow
      title={t.codes.adminCode}
      note={note}
      length={6}
      submit={async (code) => {
        await api.request("POST", "/device/admin-code", { staffToken: beheer.token, body: { code } });
        onUnlocked(code);
      }}
      onClose={onClose}
    />
  );
}
